import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, Server, KeyRound, Eye, FileCheck2, Users, Database, ArrowRight, Globe } from 'lucide-react';

type FAQItem = { id: string; question: string; answer: string };
type FAQSection = { title: string; icon: React.ComponentType<{ className?: string }>; items: FAQItem[] };

const faqSections: FAQSection[] = [
  {
    title: 'Data isolation and tenant separation',
    icon: Database,
    items: [
      {
        id: 'tenant-isolation',
        question: 'How is my company\'s data isolated from other NoQ5 customers?',
        answer: 'Every customer is provisioned as a logically isolated tenant with a unique tenant identifier enforced at the database row level via row-level security policies. OAuth tokens, CRM records, transcripts and embeddings are tagged with the tenant ID on write and filtered on every read. Application code never queries across tenants. A compromised application path cannot return another tenant\'s data because the database itself enforces the isolation.',
      },
      {
        id: 'crm-token-storage',
        question: 'Where are Salesforce, HubSpot and Pipedrive OAuth tokens stored?',
        answer: 'OAuth tokens are encrypted at rest with AES-256-GCM using organization-specific data encryption keys. The data keys are themselves encrypted by a key encryption key held in a managed KMS, so the application database never holds plaintext credentials. Tokens are rotated and refreshed automatically before expiry and revoked the moment a customer disconnects the integration.',
      },
      {
        id: 'sub-processors',
        question: 'Which sub-processors handle our CRM and conversation data?',
        answer: 'The full list of sub-processors, including the LLM provider, hosting provider, transcription provider and email delivery service, is published and version-controlled at the Subprocessors page. The list is updated before any change to the sub-processor footprint, and customers are notified of material changes.',
      },
    ],
  },
  {
    title: 'AI model training and conversation data',
    icon: ShieldCheck,
    items: [
      {
        id: 'model-training',
        question: 'Is my CRM data or conversation data used to train AI models?',
        answer: 'No. Customer CRM records, call transcripts, emails and meeting content are never used to train, fine-tune or improve any third-party or NoQ5 model. The contractual terms with the LLM provider include a zero-retention configuration: prompts and completions are not retained beyond the duration of the request and are not used for model training.',
      },
      {
        id: 'llm-provider',
        question: 'How does NoQ5 use the LLM provider, and what protections are in place?',
        answer: 'NoQ5 calls the LLM provider over TLS 1.3, with the zero-retention enterprise option enabled. No prompt or completion is logged, cached or used for training. Sensitive fields can be redacted before the request leaves the EU boundary, and prompt-injection mitigations are applied to all inbound free-text content.',
      },
      {
        id: 'recording-consent',
        question: 'How is consent handled when NoQ5 captures calls and meetings?',
        answer: 'NoQ5 only captures meetings and calls that the customer has connected and authorised. Customers configure their consent flow inside the workspace and, where required by local law, the platform supports per-participant consent prompts and per-region disclosures. NoQ5 ships with default settings aligned with EU two-party consent practice.',
      },
    ],
  },
  {
    title: 'GDPR, DPA and EU compliance',
    icon: FileCheck2,
    items: [
      {
        id: 'gdpr-role',
        question: 'Is NoQ5 a processor or a controller under GDPR?',
        answer: 'NoQ5 acts as a data processor for customer CRM and conversation data. The customer is the controller and retains decision rights over purpose and means. A Data Processing Agreement aligned with GDPR Article 28 is published and signed at contract start.',
      },
      {
        id: 'dpa',
        question: 'Where can I get the DPA and Standard Contractual Clauses?',
        answer: 'The Data Processing Agreement is published at the DPA page and includes the EU Standard Contractual Clauses for any sub-processor processing data outside the EEA. Both the DPA and SCCs are signed as part of the order form.',
      },
      {
        id: 'data-residency',
        question: 'Where is customer data physically stored?',
        answer: 'Customer data, including CRM records, transcripts and embeddings, is stored in the European Union (Frankfurt). Backups remain inside the EU. Any sub-processor that processes data outside the EEA is bound by EU SCCs and is listed on the Subprocessors page.',
      },
      {
        id: 'data-subject-rights',
        question: 'How do you support data subject access, rectification and erasure requests?',
        answer: 'NoQ5 supports the full set of GDPR data subject rights. Customers can export or delete a contact, account or opportunity from the workspace, and a dedicated privacy email handles direct data subject requests within the 30-day GDPR window. Erasure cascades to backups within the published backup retention period.',
      },
      {
        id: 'breach-notification',
        question: 'What is your breach notification commitment?',
        answer: 'In line with GDPR Article 33, NoQ5 notifies the customer of any confirmed personal-data breach without undue delay and within 72 hours of confirmation, including the categories of data, the affected scope and the remediation timeline.',
      },
    ],
  },
  {
    title: 'Encryption, access and operational security',
    icon: Lock,
    items: [
      {
        id: 'encryption',
        question: 'How is data encrypted in transit and at rest?',
        answer: 'All traffic is encrypted in transit with TLS 1.3 and HSTS. Customer data at rest is encrypted with AES-256-GCM. Database backups, file storage and message queues are encrypted under the same standard. Keys are managed by a cloud KMS and rotated on a documented schedule.',
      },
      {
        id: 'access-control',
        question: 'Who at NoQ5 can access customer data, and how is that controlled?',
        answer: 'Production access is restricted to a named on-call rotation, requires single sign-on with mandatory hardware-key MFA, and is logged and reviewed. Access is granted on a least-privilege basis and is time-boxed. NoQ5 staff cannot read customer CRM records or transcripts in the normal course of operations.',
      },
      {
        id: 'audit-trail',
        question: 'Do you provide an audit trail for CRM writes and admin actions?',
        answer: 'Yes. Every CRM write, every approval, every integration connect and disconnect, and every admin action is recorded in an immutable audit log retained for at least thirteen months. Customer admins can export the audit log for their workspace at any time.',
      },
      {
        id: 'iso-soc2',
        question: 'Are you ISO 27001 or SOC 2 certified?',
        answer: 'ISO 27001 is in progress. Until the certificate is issued, NoQ5 operates against the ISO 27001 control set and publishes its security posture and sub-processor footprint. A SOC 2 Type II report is on the roadmap.',
      },
      {
        id: 'vulnerability-management',
        question: 'How are vulnerabilities discovered and patched?',
        answer: 'Continuous dependency scanning, weekly automated security tests and yearly third-party penetration testing. Critical vulnerabilities are patched within seven days, high within thirty days. A coordinated disclosure address is published for external researchers.',
      },
    ],
  },
];

const SecurityPrivacyFAQ = () => {
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSections.flatMap((section) =>
      section.items.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    ),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://noq5.ai/' },
      { '@type': 'ListItem', position: 2, name: 'Security & Data Privacy FAQ', item: 'https://noq5.ai/security-privacy-faq' },
    ],
  };

  return (
    <Layout>
      <SEOHead
        title="Security & Data Privacy FAQ | NoQ5"
        description="Answers to the security, AI training, GDPR and data isolation questions revenue and security teams ask before deploying an AI CRM platform. EU-hosted, AES-256-GCM, zero-retention LLM."
        canonicalUrl="https://noq5.ai/security-privacy-faq"
        keywords="NoQ5 security FAQ, AI CRM data privacy, GDPR AI sales platform, EU data residency, AES-256 encryption, LLM zero retention, Salesforce HubSpot OAuth security, ISO 27001 in progress, DPA SCCs, sub-processors"
        jsonLd={[faqJsonLd, breadcrumbJsonLd]}
      />
      <div className="container px-4 md:px-6 py-12 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-12"
        >
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground">
              Security & Data Privacy FAQ
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              The questions enterprise security, RevOps and legal teams ask before NoQ5 connects to Salesforce, HubSpot or Pipedrive. Answered directly, in plain language.
            </p>
            <div className="flex flex-wrap justify-center gap-2 pt-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-card border border-border"><Server className="w-3.5 h-3.5 text-primary" /> EU-hosted (Frankfurt)</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-card border border-border"><KeyRound className="w-3.5 h-3.5 text-primary" /> AES-256-GCM at rest</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-card border border-border"><Globe className="w-3.5 h-3.5 text-primary" /> TLS 1.3 in transit</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-card border border-border"><Eye className="w-3.5 h-3.5 text-primary" /> Immutable audit trail</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-card border border-border"><Users className="w-3.5 h-3.5 text-primary" /> Zero-retention LLM</span>
            </div>
          </div>

          {/* FAQ Sections */}
          {faqSections.map((section, sectionIndex) => (
            <motion.section
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * sectionIndex }}
            >
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-foreground">
                <section.icon className="w-6 h-6 text-primary" />
                {section.title}
              </h2>
              <Accordion type="single" collapsible className="w-full space-y-4">
                {section.items.map((item) => (
                  <AccordionItem
                    key={item.id}
                    value={item.id}
                    className="bg-card rounded-xl border border-border px-6 data-[state=open]:border-primary/30 transition-colors"
                  >
                    <AccordionTrigger className="text-left hover:no-underline py-5">
                      <span className="font-semibold text-foreground pr-4">{item.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pt-2 pb-6 leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.section>
          ))}

          {/* Cross-links */}
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/security" className="bg-card rounded-xl border border-border p-6 hover:border-primary/40 transition-colors">
              <h3 className="font-semibold text-foreground mb-1">Security overview</h3>
              <p className="text-sm text-muted-foreground">Controls, encryption, access and incident response.</p>
            </Link>
            <Link to="/dpa" className="bg-card rounded-xl border border-border p-6 hover:border-primary/40 transition-colors">
              <h3 className="font-semibold text-foreground mb-1">Data Processing Agreement</h3>
              <p className="text-sm text-muted-foreground">GDPR Article 28 DPA and EU Standard Contractual Clauses.</p>
            </Link>
            <Link to="/subprocessors" className="bg-card rounded-xl border border-border p-6 hover:border-primary/40 transition-colors">
              <h3 className="font-semibold text-foreground mb-1">Sub-processors</h3>
              <p className="text-sm text-muted-foreground">The full, version-controlled list of sub-processors.</p>
            </Link>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-primary rounded-2xl p-8 md:p-12 text-center"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-primary-foreground">Need a security review or a signed DPA?</h3>
            <p className="text-lg mb-8 text-primary-foreground/80 max-w-2xl mx-auto">
              Send the security questionnaire or DPA. A founder responds within one business day.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-background text-primary hover:bg-background/90 font-semibold px-8 py-6 text-lg rounded-xl"
                asChild
              >
                <a href="mailto:batiste@noq5.ai?subject=Security%20%26%20DPA%20review">
                  Contact security
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-semibold px-8 py-6 text-lg rounded-xl"
                asChild
              >
                <Link to="/?scrollTo=demo">Book a demo</Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default SecurityPrivacyFAQ;
